/**
 * AppUpgradeJobsAgGridHook
 *
 * Grid for /ota/jobs (the "Upgrade Jobs List" — AppUpgradeConfig campaigns).
 * Uses AG Grid's server-side row model so the page fetches one page of
 * campaigns at a time over the LiveView socket rather than the whole
 * app_upgrade_configs table. See PlatformWeb.ApplicationUpgradeLive.Index
 * for the query side.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 25;

const STATUS_COLORS = {
  completed: "#16A34A",
  in_progress: "#2563EB",
  failed: "#DC2626",
  pending: "#D97706",
};

const statusBadge = (value) => {
  if (!value) return "";
  const bg = STATUS_COLORS[value] || "#6B7280";
  const label = value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:9999px;
    font-size:11px;font-weight:600;white-space:nowrap;">${label}</span>`;
};

const dateCell = (params) => {
  if (!params.value) return "";
  const [date, time] = String(params.value).split("T");
  return `<div>
    <div>${date || params.value}</div>
    <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${(time || "").split(".")[0]}</div>
  </div>`;
};

const AppUpgradeJobsAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("AppUpgradeJobsAgGridHook: agGrid not found on window");
      return;
    }

    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id } = params.data;
      const btn = document.createElement("button");
      btn.textContent = "Delete";
      btn.style.cssText = "color:#DC2626;font-size:12px;cursor:pointer;background:none;border:none;padding:0;";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (window.confirm("Delete this upgrade job?")) {
          self.pushEvent("delete", { id: String(id) });
        }
      });
      return btn;
    };

    const columnDefs = [
      {
        headerName: "Batch Code",
        field: "batch_code",
        flex: 1,
        minWidth: 150,
        cellRenderer: (params) => {
          if (!params.data) return "";
          return `<div>
            <div style="font-weight:500;color:var(--ag-data-color);">${params.value || ""}</div>
            <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">Job ID: ${params.data.id}</div>
          </div>`;
        },
      },
      {
        headerName: "Package",
        colId: "package",
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        cellRenderer: (params) => {
          if (!params.data) return "";
          const { package_version_name, package_model, package_app_version } = params.data;
          if (!package_version_name) return '<span style="color:var(--ag-secondary-foreground-color);">N/A</span>';
          return `<div>
            <div style="font-weight:500;color:var(--ag-data-color);">${package_version_name}</div>
            <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${package_model || "N/A"} · v${package_app_version || "N/A"}</div>
          </div>`;
        },
      },
      {
        headerName: "Devices",
        field: "update_count",
        width: 110,
        cellRenderer: (params) =>
          params.value == null ? "" : `${params.value} devices`,
      },
      {
        headerName: "Mode",
        field: "force_upgrade",
        width: 130,
        sortable: false,
        cellRenderer: (params) =>
          params.value
            ? '<span style="color:#DC2626;font-size:12px;font-weight:600;">Force Upgrade</span>'
            : '<span style="color:#16A34A;font-size:12px;font-weight:600;">Normal</span>',
      },
      { headerName: "Created By", field: "created_by", width: 120 },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        cellRenderer: (p) => statusBadge(p.value),
      },
      { headerName: "Created", field: "inserted_at", width: 160, cellRenderer: dateCell },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 100,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "app_upgrade_jobs:rows",
      refreshEvent: "app_upgrade_jobs:refresh",
      pageSize: PAGE_SIZE,
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default AppUpgradeJobsAgGridHook;
